import React, { useEffect, useMemo, useState, useContext } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSearch,
  FaChevronUp,
  FaChevronDown,
  FaTrash,
  FaEdit,
} from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const BASE_URL = "https://nissi-india.com";

const ContractorPaymentListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(true);

  // -------------------- localStorage helpers --------------------
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined && val !== "" ? val : fallback;
  };

  const toStr = (val, fallback = "") => {
    if (val === null || val === undefined) return fallback;
    const str = String(val).trim();
    return str === "" ? fallback : str;
  };

  // ✅ localStorage values
  const finYearId = useMemo(() => toStr(getLS("finYearId")), []);
  const finYear = useMemo(() => toStr(getLS("finYear")), []);
  const branchIdLS = useMemo(
    () => toStr(getLS("branchId") || getLS("branchid")),
    []
  );
  const branchTypeLS = useMemo(
    () => toStr(getLS("branchType") || getLS("branchtype"), "HEAD"),
    []
  );
  const userId = useMemo(
    () =>
      toStr(
        getLS("userId") || getLS("loginuserid") || getLS("loginUserId")
      ),
    []
  );

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branch": branchTypeLS,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userId]
  );

  // Search state
  const [searchData, setSearchData] = useState({
    fromDate: "",
    toDate: "",
    contractorName: "",
    contractorId: "",
    projectId: "",
    billNo: "",
  });

  // Dropdown states
  const [ddlContractorNames, setDdlContractorNames] = useState([]);
  const [ddlContractorCodes, setDdlContractorCodes] = useState([]);
  const [ddlProjects, setDdlProjects] = useState([]);
  const [ddlBills, setDdlBills] = useState([]);

  // Table Data
  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ddlLoading, setDdlLoading] = useState(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);

  const allRowIds = useMemo(
    () => (Array.isArray(paymentData) ? paymentData : []).map((x) => x.contractorPayId),
    [paymentData]
  );

  const isAllSelected = useMemo(() => {
    if (!allRowIds.length) return false;
    return allRowIds.every((id) => selectedIds.includes(id));
  }, [allRowIds, selectedIds]);

  const selectedCount = selectedIds.length;

  const handleSearchChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  // -------------------- Dropdown APIs --------------------
  const loadContractorNames = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/contractorPayment/contractors`,
        {
          params: { branchid: branchIdLS },
          headers: commonHeaders,
        }
      );

      if (res.data?.success) {
        setDdlContractorNames(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setDdlContractorNames([]);
      }
    } catch (err) {
      console.error(
        "Contractor Names Error:",
        err?.response?.data || err.message
      );
      toast.error("Failed to load Contractor Names");
      setDdlContractorNames([]);
    }
  };

  const loadContractorCodes = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/contractorPayment/contractorCodes`,
        {
          params: { branchid: branchIdLS },
          headers: commonHeaders,
        }
      );

      if (res.data?.success) {
        setDdlContractorCodes(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setDdlContractorCodes([]);
      }
    } catch (err) {
      console.error(
        "Contractor Codes Error:",
        err?.response?.data || err.message
      );
      toast.error("Failed to load Contractor Codes");
      setDdlContractorCodes([]);
    }
  };

  const loadProjects = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/contractorPayment/projects`, {
        params: { branchid: branchIdLS, branchtype: branchTypeLS },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setDdlProjects(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setDdlProjects([]);
      }
    } catch (err) {
      console.error("Projects Error:", err?.response?.data || err.message);
      toast.error("Failed to load Projects");
      setDdlProjects([]);
    }
  };

  const loadBills = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/contractorPayment/bills`, {
        params: { branchid: branchIdLS },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setDdlBills(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setDdlBills([]);
      }
    } catch (err) {
      console.error("Bills Error:", err?.response?.data || err.message);
      toast.error("Failed to load Bill Numbers");
      setDdlBills([]);
    }
  };

  const loadDropdowns = async () => {
    if (!branchIdLS) {
      toast.error("BranchId not found in localStorage!");
      return;
    }

    setDdlLoading(true);
    try {
      await Promise.all([
        loadContractorNames(),
        loadContractorCodes(),
        loadProjects(),
        loadBills(),
      ]);
    } finally {
      setDdlLoading(false);
    }
  };

  // -------------------- Listing API --------------------
  const loadListing = async () => {
    try {
      if (!branchIdLS) {
        toast.error("BranchId not found in localStorage!");
        return;
      }

      setLoading(true);
      setSelectedIds([]);

      const params = new URLSearchParams();
      params.append("branchid", branchIdLS);

      if (searchData.fromDate) params.append("fromDate", searchData.fromDate);
      if (searchData.toDate) params.append("toDate", searchData.toDate);
      if (searchData.contractorId)
        params.append("contractorId", searchData.contractorId);
      if (searchData.contractorName)
        params.append("contractorName", searchData.contractorName);
      if (searchData.projectId)
        params.append("projectId", searchData.projectId);
      if (searchData.billNo) params.append("billNo", searchData.billNo);

      const res = await axios.get(
        `${BASE_URL}/api/contractorPayment/list?${params.toString()}`,
        {
          headers: commonHeaders,
        }
      );

      if (res.data?.success) {
        setPaymentData(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setPaymentData([]);
        toast.warn("No data found!");
      }
    } catch (err) {
      console.error("Listing Error:", err?.response?.data || err.message);
      toast.error("Error loading listing!");
      setPaymentData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => loadListing();

  const handleAddNew = () => {
    navigate("/PaymentVouchers/ContractorPaymentEntry");
  };

  const handleBillClick = (row) => {
    navigate("/PaymentVouchers/ContractorPaymentEntry", {
      state: {
        editMode: true,
        contractorPayId: row.contractorPayId,
      },
    });
  };

  const formatAmount = (amount) => Number(amount || 0).toFixed(2);

  // -------------------- Checkbox handlers --------------------
  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds([...allRowIds]);
    }
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  // -------------------- Delete / Update buttons --------------------
  const handleUpdateSelected = () => {
    if (selectedIds.length !== 1) {
      toast.warn("Please select exactly ONE row to update.");
      return;
    }

    const id = selectedIds[0];
    navigate("/PaymentVouchers/ContractorPaymentEntry", {
      state: {
        editMode: true,
        contractorPayId: id,
      },
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one row to delete.");
      return;
    }

    if (!userId) {
      toast.error("UserId missing in localStorage!");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      await Promise.all(
        selectedIds.map((id) =>
          axios.delete(`${BASE_URL}/api/contractorPayment/delete/${id}`, {
            params: { loginuserid: userId, branchid: branchIdLS },
            headers: commonHeaders,
          })
        )
      );

      toast.success("Deleted successfully!");
      setSelectedIds([]);
      await loadListing();
    } catch (err) {
      console.error("Delete Error:", err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || "Delete failed!");
    }
  };

  useEffect(() => {
    loadDropdowns();
    loadListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">CONTRACTOR PAYMENT LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Contractor Payment Listing
            </h1>

            <div className="flex items-center gap-3">
              {ddlLoading && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading dropdowns...
                </span>
              )}

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={searchData.fromDate}
                    onChange={(e) => handleSearchChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={searchData.toDate}
                    onChange={(e) => handleSearchChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div />

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contractor Name
                  </label>
                  <select
                    value={searchData.contractorName}
                    onChange={(e) => handleSearchChange("contractorName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlContractorNames.map((x, index) => (
                      <option key={`${x.value || index}-${x.label || index}`} value={x.label}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contractor Code
                  </label>
                  <select
                    value={searchData.contractorId}
                    onChange={(e) => handleSearchChange("contractorId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlContractorCodes.map((x, index) => (
                      <option key={`${x.value || index}-${x.label || index}`} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div />

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Project Name
                  </label>
                  <select
                    value={searchData.projectId}
                    onChange={(e) => handleSearchChange("projectId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlProjects.map((x, index) => (
                      <option key={`${x.value || index}-${x.label || index}`} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Bill Number
                  </label>
                  <select
                    value={searchData.billNo}
                    onChange={(e) => handleSearchChange("billNo", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlBills.map((x, index) => (
                      <option key={`${x.value || index}-${x.label || index}`} value={x.label}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center items-center gap-6 mb-6">
                <button
                  onClick={handleAddNew}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPlus />
                  Add New
                </button>

                <button
                  onClick={handleSearch}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSearch />
                  Search
                </button>
              </div>
            </div>
          )}

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 cursor-pointer"
                        title="Select All"
                      />
                    </th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Sl.No</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Bill Number</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Date</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Contractor Name</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Project Name</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Amount</th>
                    <th className="px-4 py-3 text-center font-semibold">Pending Amount</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center py-6">
                        Loading...
                      </td>
                    </tr>
                  ) : paymentData.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-6">
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    paymentData.map((row, index) => (
                      <tr
                        key={row.contractorPayId || index}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(row.contractorPayId)}
                            onChange={() => toggleRow(row.contractorPayId)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>

                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {row.slNo}
                        </td>

                        <td
                          className={`px-4 py-3 text-center border-r cursor-pointer font-semibold underline ${
                            isDark ? "border-gray-700 text-blue-400" : "border-gray-200 text-blue-600"
                          }`}
                          onClick={() => handleBillClick(row)}
                          title="Click to Edit"
                        >
                          {row.billNumber}
                        </td>

                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {row.date}
                        </td>

                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {row.contractorName}
                        </td>

                        <td className={`px-4 py-3 text-left border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {row.projectName}
                        </td>

                        <td className={`px-4 py-3 text-right border-r font-mono ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {formatAmount(row.amount)}
                        </td>

                        <td className={`px-4 py-3 text-right font-mono ${isDark ? "text-orange-400" : "text-orange-600"}`}>
                          {formatAmount(row.pendingAmount)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap justify-center items-center gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Selected: <b>{selectedCount}</b>
            </div>

            <button
              onClick={handleUpdateSelected}
              disabled={selectedIds.length !== 1}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg transform transition-all duration-200 text-white ${
                selectedIds.length === 1 ? `${buttonClass} ${hoverClass}` : "bg-gray-400 cursor-not-allowed"
              }`}
              title="Select 1 row to update"
            >
              <FaEdit />
              Update
            </button>

            <button
              onClick={handleDeleteSelected}
              disabled={selectedIds.length === 0}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg transform transition-all duration-200 text-white ${
                selectedIds.length > 0 ? "bg-red-600 hover:bg-red-700" : "bg-gray-400 cursor-not-allowed"
              }`}
              title="Select one or more rows to delete"
            >
              <FaTrash />
              Delete
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default ContractorPaymentListing;